import type {
  FormMenuAvailabilityType,
  FormMenuDateRange,
  FormMenuItem,
  FormMenusCalendarMap,
  UploadFormMenuImageResponse,
} from '../types/form-menu.types';
import { resolveFormMenuImageUrl } from './resolve-form-menu-image-url';

function readString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return fallback;
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function readNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function readNullableString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = readString(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => readString(item).trim())
    .filter((item) => item.length > 0);
}

function readBoolean(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  return fallback;
}

function readWeekdays(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((day) => Number(day))
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7);
}

function readAvailabilityType(value: unknown): FormMenuAvailabilityType {
  if (value === 'weekly' || value === 'dates') {
    return value;
  }
  return null;
}

function readDateRanges(value: unknown): FormMenuDateRange[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const result: FormMenuDateRange[] = [];
  value.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') {
      return;
    }
    const record = raw as Record<string, unknown>;
    const startMenuDate = readNullableString(record.startMenuDate);
    const endMenuDate = readNullableString(record.endMenuDate);
    if (!startMenuDate || !endMenuDate) {
      return;
    }
    const formMenuDateId = readNumber(record.formMenuDateId, 0) || undefined;
    const range: FormMenuDateRange = {
      id:
        formMenuDateId !== undefined
          ? `dr-${String(formMenuDateId)}`
          : `dr-${String(index)}-${startMenuDate}`,
      startMenuDate,
      endMenuDate,
    };
    if (formMenuDateId !== undefined) {
      range.formMenuDateId = formMenuDateId;
    }
    result.push(range);
  });
  return result;
}

export function normalizeFormMenu(raw: unknown): FormMenuItem | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const formMenuCode = readString(record.formMenuCode).trim();
  if (!formMenuCode) {
    return null;
  }

  const isNoMenu =
    readBoolean(record.isNoMenu, false) || formMenuCode === 'NO_MENU';
  const formMenuId = readNumber(record.formMenuId) || readNumber(record.id);

  // Synthetic NO_MENU rows may omit formMenuId.
  if (!formMenuId && !isNoMenu) {
    return null;
  }

  const isActive = readBoolean(record.isActive, true);
  const menuImage = readNullableString(record.menuImage);
  const description = readNullableString(record.description) ?? '';

  return {
    formMenuId: formMenuId || 0,
    formMenuCode,
    branchCode: readString(record.branchCode),
    name: readString(record.name),
    description,
    price: readNumber(record.price, 0),
    menuImage,
    photoUrl: resolveFormMenuImageUrl(menuImage) ?? menuImage,
    active: isActive,
    isActive,
    isMandatory: readBoolean(record.isMandatory, false),
    availabilityType: readAvailabilityType(record.availabilityType),
    formCodes: readStringArray(record.formCodes),
    weekdays: readWeekdays(record.weekdays),
    dateRanges: readDateRanges(record.dateRanges),
    isNoMenu,
    totalBooking: readNullableNumber(
      record.totalBooking ?? record.total_booking,
    ),
    bookingCount: readNumber(record.bookingCount, 0),
    guestCount: readNumber(record.guestCount, 0),
    createdAt: readNullableString(record.createdAt) ?? undefined,
    updatedAt: readNullableString(record.updatedAt) ?? undefined,
  };
}

export function normalizeFormMenuDate(raw: unknown): FormMenuDateRange | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const startMenuDate = readNullableString(record.startMenuDate);
  const endMenuDate = readNullableString(record.endMenuDate);
  if (!startMenuDate || !endMenuDate) {
    return null;
  }

  const formMenuDateId = readNumber(record.formMenuDateId, 0) || undefined;
  const range: FormMenuDateRange = {
    id:
      formMenuDateId !== undefined
        ? `dr-${String(formMenuDateId)}`
        : `dr-${startMenuDate}-${endMenuDate}`,
    startMenuDate,
    endMenuDate,
  };
  if (formMenuDateId !== undefined) {
    range.formMenuDateId = formMenuDateId;
  }
  return range;
}

export function normalizeUploadFormMenuImageResponse(
  raw: unknown,
): UploadFormMenuImageResponse {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 0,
      fileName: '',
      filePath: '',
      fileType: '',
      fileUrl: '',
      storedDate: '',
    };
  }

  const record = raw as Record<string, unknown>;
  return {
    id: readNumber(record.id),
    fileName: readString(record.fileName),
    filePath: readString(record.filePath),
    fileType: readString(record.fileType),
    fileUrl: readString(record.fileUrl),
    storedDate: readString(record.storedDate),
  };
}

export function isPersistedFormMenuImageUrl(
  url?: string | null,
): url is string {
  if (!url) {
    return false;
  }
  return (
    !url.startsWith('blob:') &&
    !url.startsWith('data:') &&
    url.trim().length > 0
  );
}

export function normalizeFormMenusCalendar(raw: unknown): FormMenusCalendarMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const record = raw as Record<string, unknown>;
  const calendar: FormMenusCalendarMap = {};

  for (const [dateKey, value] of Object.entries(record)) {
    if (!Array.isArray(value)) {
      calendar[dateKey] = [];
      continue;
    }

    calendar[dateKey] = value
      .map((item) => normalizeFormMenu(item))
      .filter((item): item is FormMenuItem => item !== null);
  }

  return calendar;
}

export function normalizeFormMenusList(raw: unknown): FormMenuItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => normalizeFormMenu(item))
    .filter((item): item is FormMenuItem => item !== null);
}
